/**
 * OfferteComparator Component
 * Sezione comparatore offerte luce/gas con risparmio potenziale
 * Issue #35: Colonna "Spesa Attuale" + estensioni
 */

import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './OfferteComparator.css';

/**
 * Etichetta e descrizione di ogni fonte dati, una sola volta.
 *
 * 2026-09-24 engineer [DECISIONE MIRKO]: 'ARERA' NON e' in questa mappa, ed e' il
 * punto della modifica. Quelle righe non vengono da ARERA — lo scraper interroga
 * una pagina che risponde 404 e scrive due costanti hard-coded (luce 0.089 €/kWh,
 * gas 0.7383 €/m³), e nel DB sono 2721 righe su 2721 con quei due soli valori dal
 * 01/04/2026. Finche' non c'e' un feed vero, quelle offerte non si mostrano: sono
 * escluse a monte dall'API, e se per qualsiasi ragione una arrivasse qui, non
 * essendo nella mappa non produce nessun badge.
 *
 * 'portale_offerte' e' cosa diversa e va tenuta: e' il Portale Offerte ARERA,
 * dati veri e aggiornati (oltre 3,2M righe, ultimo scrape oggi).
 */
const FONTI_ETICHETTE = {
  portale_offerte: { etichetta: '📊 Portale Offerte', titolo: 'Dati dal Portale Offerte ARERA (open data)' },
  facile:          { etichetta: '🌐 Facile.it',       titolo: 'Dati da Facile.it' },
  manual:          { etichetta: '🧾 Fattura Mirko',   titolo: 'Condizioni reali da una bolletta di Mirko, non un\'offerta di listino' }
};

/**
 * Format last update timestamp
 */
function formatLastUpdate(timestamp) {
  if (!timestamp) return 'N/D';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return `${diffMins} minuti fa`;
  }
  
  if (diffHours < 24) {
    return `${diffHours} ore fa`;
  }
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} giorni fa`;
}

/**
 * Check if data is stale (>24h old)
 */
function isDataStale(timestamp) {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffHours = (now - date) / (1000 * 60 * 60);
  
  return diffHours > 24;
}
/**
 * Format offer timestamp based on source (Issue #221)
 * - Manual offers: show "Dato manuale (inserito il ...)" using created_at
 * - Automatic offers: show "Aggiornato ..." using updated_at with relative time
 */
function formatOfferTimestamp(offerta) {
  const isManual = offerta.fonte === 'manual';
  
  if (isManual) {
    // For manual offers, show created_at as "inserted on" date
    const date = offerta.created_at ? new Date(offerta.created_at) : null;
    if (!date || isNaN(date.getTime())) {
      return { text: '📝 Dato manuale', title: 'Dato inserito manualmente' };
    }
    const formattedDate = date.toLocaleDateString('it-IT');
    return {
      text: `🧾 Fattura Mirko (${formattedDate})`,
      title: `Dato inserito manualmente il ${formattedDate}`
    };
  } else {
    // For automatic offers, show relative time since updated_at
    const timestamp = offerta.updated_at;
    if (!timestamp) {
      return { text: '🕒 N/D', title: 'Timestamp non disponibile' };
    }
    return {
      text: `🕒 ${formatLastUpdate(timestamp)}`,
      title: `Ultimo aggiornamento automatico: ${new Date(timestamp).toLocaleString('it-IT')}`
    };
  }
}

function OfferteComparator({ tipo = 'luce', consumoAnnuo = null }) {
  const [offerte, setOfferte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [spesaAttuale, setSpesaAttuale] = useState(null);
  const [consumoReale, setConsumoReale] = useState(null);
  const [consumoFonte, setConsumoFonte] = useState(null);
  const [warning, setWarning] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [noBollette, setNoBollette] = useState(false);
  const [noBolletteMessage, setNoBolletteMessage] = useState(null);
  const [mercatoTipo, setMercatoTipo] = useState(null);
  const [filtroMercatoApplicato, setFiltroMercatoApplicato] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const DEFAULT_LIMIT = 10;

  const fetchOfferte = async () => {
    try {
      setLoading(true);
      setShowAll(false); // Reset on new fetch
      setError(null);
      setWarning(null);
      setNoBollette(false);
      setNoBolletteMessage(null);

      // Query params: tipo obbligatorio, consumo_annuo opzionale (backend calcola da bollette DB)
      const params = new URLSearchParams({ tipo });
      if (consumoAnnuo) {
        params.append('consumo_annuo', consumoAnnuo);
      }

      const response = await api.get(`/offerte/confronta?${params}`);
      const data = response.data.data;

      setOfferte(data.offerte_top || []);
      setSpesaAttuale(data.spesa_attuale_stimata);
      setConsumoReale(data.consumo_annuo);
      setConsumoFonte(data.consumo_annuo_fonte || null);
      setWarning(data.warning || null);
      setLastUpdate(data.last_update || null);
      setNoBollette(data.no_bollette || false);
      setNoBolletteMessage(data.no_bollette ? data.message : null);
      setMercatoTipo(data.mercato_tipo || null);
      setFiltroMercatoApplicato(data.filtro_mercato_applicato || false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      console.error('[OfferteComparator] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOfferte();
  }, [tipo, consumoAnnuo]);

  if (loading) {
    return (
      <div className="comparator-loading">
        Caricamento offerte...
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparator-error">
        <p>❌ Errore: {error}</p>
        <button onClick={fetchOfferte} className="btn-retry">
          Riprova
        </button>
      </div>
    );
  }

  // Issue #166: Gas senza bollette — mostra messaggio dedicato, non dati inventati
  if (noBollette) {
    return (
      <div className="comparator-empty comparator-no-bollette">
        <p className="empty-title">📋 Nessuna bolletta {tipo} caricata</p>
        <p className="empty-subtitle">
          {noBolletteMessage || `Carica almeno una bolletta ${tipo} per vedere il confronto offerte e il risparmio potenziale`}
        </p>
      </div>
    );
  }

  if (warning) {
    return (
      <div className="comparator-empty">
        <p className="empty-title">⚠️ Dati insufficienti</p>
        <p className="empty-subtitle">{warning}</p>
      </div>
    );
  }

  if (offerte.length === 0) {
    return (
      <div className="comparator-empty">
        <p className="empty-title">📭 Nessuna offerta disponibile</p>
        <p className="empty-subtitle">
          Stiamo raccogliendo dati dai provider per {tipo === 'luce' ? 'energia elettrica' : 'gas'}.
          <br />Le offerte saranno disponibili a breve.
        </p>
      </div>
    );
  }

  const unit = tipo === 'luce' ? 'kWh' : 'm³';
  const hasBollette = spesaAttuale !== null && spesaAttuale > 0;
  
  const displayedOfferte = showAll ? offerte : offerte.slice(0, DEFAULT_LIMIT);
  const hasMore = offerte.length > DEFAULT_LIMIT;

  return (
    <div className="comparator-container">
      {/* Header Info */}
      <div className="comparator-header">
        <p>
          📊 Consumo annuo stimato: <strong>{consumoReale} {unit}</strong>
          {consumoFonte && (
            <span className={`fonte-badge fonte-${consumoFonte}`} title={`Fonte: ${consumoFonte}`}>
              {consumoFonte === 'storico-bolletta' && ' ✅ da bolletta'}
              {consumoFonte === 'calcolato-stagionale' && ' 📈 stagionale'}
              {consumoFonte === 'annualizzato' && ' ⚠️ stima lineare'}
              {consumoFonte === 'parametro-query' && ' 📝 inserito'}
              {consumoFonte === 'stimato-da-spesa' && ' 🔄 ricalcolato da spesa'}
              {consumoFonte === 'media-istat' && ' 📊 media ISTAT'}
            </span>
          )}
        </p>
        {hasBollette && (
          <p>
            💰 Spesa attuale stimata: <strong>€ {spesaAttuale}/anno</strong>
          </p>
        )}
      </div>

      {/* Issue #174/#178: Badge info mercato libero */}
      {filtroMercatoApplicato && mercatoTipo === 'libero' && (
        <div className="alert-info-mercato">
          <span className="alert-icon">ℹ️</span>
          <span className="alert-text">
            <strong>Mercato libero:</strong> Offerte maggior tutela ARERA nascoste (non disponibili per clienti in mercato libero).
          </span>
        </div>
      )}

      {/* Provider info */}
      {offerte.length > 0 && (
        <div className="provider-summary">
          <p>
            📋 <strong>{offerte.length}</strong> offerte disponibili da <strong>{new Set(offerte.map(o => o.provider)).size}</strong> fornitori diversi
          </p>
        </div>
      )}

      {/* Tabella Offerte */}
      <div className="table-wrapper">
        <table className="offerte-table">
          <thead>
            <tr>
              <th className="col-provider">Provider</th>
              <th className="col-offerta">Offerta</th>
              <th className="col-spesa-attuale">
                La Tua Spesa Attuale
                <span className="subtitle">(stimata annua)</span>
              </th>
              <th className="col-spesa-stimata">Spesa Stimata</th>
              <th className="col-risparmio">Risparmio</th>
              <th className="col-dettagli">Dettagli</th>
            </tr>
          </thead>
          <tbody>
            {displayedOfferte.map((offerta) => {
              const isCostlier = hasBollette && offerta.risparmio_euro < 0;
              
              return (
                <tr 
                  key={offerta.id}
                  className={`
                    ${offerta.is_best ? 'row-best' : ''} 
                    ${isCostlier ? 'row-costlier' : ''}
                  `}
                >
                  {/* Provider */}
                  <td className="col-provider">
                    <strong>{offerta.provider}</strong>
                    <div className="provider-source">
                      {/* 2026-09-24 engineer [DECISIONE MIRKO]: rimosso il badge
                          della fonte di tutela e il suo tooltip di provenienza.
                          Quelle righe non vengono dall'autorita': lo scraper
                          interroga una pagina che risponde 404 e scrive due
                          costanti hard-coded, quindi il badge era una
                          dichiarazione di provenienza falsa. Ora quelle offerte
                          sono anche escluse a monte dall'API.
                          Il tooltip precedente attribuiva a Facile.it QUALSIASI
                          fonte diversa da quella, quindi era sbagliato anche su
                          portale_offerte e manual: ora ogni fonte ha la sua
                          descrizione, presa da FONTI_ETICHETTE. */}
                      {offerta.fonte && FONTI_ETICHETTE[offerta.fonte] && (
                        <span
                          className={`badge badge-fonte badge-fonte-${offerta.fonte.toLowerCase()}`}
                          title={FONTI_ETICHETTE[offerta.fonte].titolo}
                        >
                          {FONTI_ETICHETTE[offerta.fonte].etichetta}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Offerta */}
                  <td className="col-offerta">
                    <div className="offerta-name">
                      {offerta.nome_offerta}
                    </div>
                    <div className="offerta-badges">
                      {offerta.is_best && (
                        <span className="badge badge-best">⭐ Miglior offerta</span>
                      )}
                      {offerta.is_hot_deal && !offerta.is_best && (
                        <span className="badge badge-hot">🔥 Hot deal</span>
                      )}
                      {offerta.risparmio_warning && (
                        <span className="badge badge-warning" title={offerta.risparmio_warning}>⚠️ Verifica</span>
                      )}
                      {offerta.energia_verde && (
                        <span className="badge badge-green">🌱 Energia verde</span>
                      )}
                    </div>
                  </td>

                  {/* Spesa Attuale */}
                  <td className="col-spesa-attuale">
                    {hasBollette ? (
                      <div 
                        className="spesa-attuale" 
                        title="Calcolato dai tuoi consumi degli ultimi 12 mesi"
                      >
                        € {spesaAttuale}
                      </div>
                    ) : (
                      <div 
                        className="spesa-nd" 
                        title="Carica almeno una bolletta per calcolare la spesa"
                      >
                        N/D
                      </div>
                    )}
                  </td>

                  {/* Spesa Stimata */}
                  <td className="col-spesa-stimata">
                    {offerta.spesa_stimata_annua !== null ? (
                      <>
                        <strong>€ {offerta.spesa_stimata_annua.toFixed(2)}</strong>
                        <div className="spesa-details">
                          <span>€ {offerta.prezzo_kwh.toFixed(3)}/{unit}</span>
                          <span>€ {offerta.quota_fissa_mensile.toFixed(2)}/mese</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="spesa-details">
                          <span>€ {offerta.prezzo_kwh.toFixed(3)}/{unit}</span>
                          <span>€ {offerta.quota_fissa_mensile.toFixed(2)}/mese</span>
                        </div>
                        <div className="spesa-nd" title="Carica una bolletta per calcolare la spesa stimata">
                          Spesa N/D
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Risparmio */}
                  {/* Backend convention: risparmio_pct > 0 = risparmi, < 0 = costa di più */}
                  <td className="col-risparmio">
                    {offerta.risparmio_pct !== null ? (
                      <div>
                        {isCostlier ? (
                          <div className="risparmio-negative">
                            <span className="badge badge-costlier">❌ Più cara</span>
                            <div className="risparmio-amount">
                              +€ {Math.abs(offerta.risparmio_euro).toFixed(2)}/anno
                            </div>
                            <div className="risparmio-pct-negative">
                              +{Math.abs(offerta.risparmio_pct).toFixed(1)}%
                            </div>
                          </div>
                        ) : (
                          <div className="risparmio-positive">
                            <div className="risparmio-pct">
                              {offerta.risparmio_pct > 0 ? '+' : ''}{offerta.risparmio_pct.toFixed(1)}%
                            </div>
                            <div className="risparmio-amount">
                              Risparmi € {offerta.risparmio_euro.toFixed(2)}/anno
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="risparmio-na">–</span>
                    )}
                  </td>

                  {/* Dettagli */}
                  <td className="col-dettagli">
                    <div className="dettagli-extra">
                      {offerta.tipo_prezzo && (
                        <span className={`detail-item badge-tipo-prezzo badge-tipo-${offerta.tipo_prezzo}`}>
                          {offerta.tipo_prezzo === 'fisso' ? '🔒 Prezzo fisso' : '📈 Prezzo variabile'}
                        </span>
                      )}
                      {offerta.durata_mesi && (
                        <span className="detail-item">
                          📅 {offerta.durata_mesi} mesi bloccato
                        </span>
                      )}
                      {!offerta.durata_mesi && (
                        <span className="detail-item text-muted">
                          Variabile
                        </span>
                      )}
                      {offerta.tipo_tariffa && (
                        <span className="detail-item">
                          ⚡ {offerta.tipo_tariffa}
                        </span>
                      )}
                      {offerta.prezzo_f1 && offerta.prezzo_f2 && offerta.prezzo_f3 && (
                        <span className="detail-item detail-fasce" title="Prezzi per fascia oraria">
                          F1: €{offerta.prezzo_f1.toFixed(4)} | F2: €{offerta.prezzo_f2.toFixed(4)} | F3: €{offerta.prezzo_f3.toFixed(4)}
                        </span>
                      )}
                      {offerta.alpha && (
                        <span className="detail-item" title="Spread su PUN (prezzo variabile)">
                          α: {offerta.alpha.toFixed(4)} €/kWh
                        </span>
                      )}
                      {offerta.url_offerta ? (
                        <>
                          <a
                            href={offerta.url_offerta}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-offerta"
                            title={offerta.fonte === 'portale_offerte' ? 'Sito del fornitore (offerta pubblicata su Portale Offerte ARERA)' : 'Vai alla scheda offerta sul sito del fornitore'}
                          >
                            🔗 {offerta.fonte === 'portale_offerte' ? 'Sito fornitore' : "Vai all'offerta"}
                          </a>
                          {offerta.fonte === 'portale_offerte' && offerta.cod_offerta && (
                            <a
                              href={`https://www.ilportaleofferte.it/portaleOfferte/it/portale_offerte.page`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-offerta link-arera"
                              title={`Cerca questa offerta sul Portale ARERA (cod: ${offerta.cod_offerta})`}
                              style={{marginLeft: '6px', fontSize: '0.8em', opacity: 0.8}}
                            >
                              📋 ARERA
                            </a>
                          )}
                        </>
                      ) : (
                        offerta.fonte === 'portale_offerte' && offerta.cod_offerta ? (
                          <a
                            href={`https://www.ilportaleofferte.it/portaleOfferte/it/portale_offerte.page`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-offerta"
                            title={`Cerca su Portale Offerte ARERA (cod: ${offerta.cod_offerta})`}
                          >
                            📋 Cerca su ARERA
                          </a>
                        ) : (
                          <span className="link-na text-muted">N/D</span>
                        )
                      )}
                      {(offerta.updated_at || offerta.created_at) && (
                        <span 
                          className="detail-item updated-at"
                          title={formatOfferTimestamp(offerta).title}
                        >
                          {formatOfferTimestamp(offerta).text}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Show All / Show Less button */}
      {hasMore && (
        <div className="show-all-container">
          <button 
            className="btn-show-all"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                ▲ Mostra meno (top {DEFAULT_LIMIT})
              </>
            ) : (
              <>
                ▼ Mostra tutte le {offerte.length} offerte
              </>
            )}
          </button>
          {!showAll && <p className="show-all-hint">Visualizzate top {displayedOfferte.length} offerte per risparmio</p>}
        </div>
      )}

      {/* Footer Note */}
      <div className="comparator-footer">
        {lastUpdate && (
          <p className="last-update-info">
            🕐 Ultimo aggiornamento offerte automatiche: <strong>{formatLastUpdate(lastUpdate)}</strong>
            {isDataStale(lastUpdate) && (
              <span className="stale-warning"> ⚠️ Dati non aggiornati da più di 24h, riprova tra poco</span>
            )}
            <span className="manual-note" title="Le offerte manuali (✏️) hanno dati statici inseriti al seed"> ℹ️ Escluse offerte manuali</span>
          </p>
        )}
        <p>
          ℹ️ <strong>Nota:</strong> I costi stimati includono trasporto, oneri di sistema, accise e IVA 10% (componenti regolate ARERA uguali per tutti i fornitori).
        </p>
        <p>
          Il risparmio è calcolato rispetto alla spesa attuale basata sulle bollette degli ultimi 12 mesi.
        </p>
        {consumoFonte === 'calcolato-stagionale' && (
          <p>
            📈 Il consumo annuo tiene conto della stagionalità (inverno/estate) per una stima più accurata.
          </p>
        )}
        {consumoFonte === 'annualizzato' && (
          <p>
            ⚠️ Il consumo annuo è stimato con annualizzazione lineare — la precisione migliora con più bollette caricate.
          </p>
        )}
        {consumoFonte === 'stimato-da-spesa' && (
          <p>
            🔄 Il consumo annuo è stato ricalcolato dalla spesa perché il dato in bolletta risultava incoerente. Carica più bollette per una stima più precisa.
          </p>
        )}
        {consumoFonte === 'media-istat' && (
          <p>
            📊 Il consumo annuo usa la media nazionale ISTAT perché i dati disponibili non sono sufficienti per un calcolo preciso. Carica più bollette o inserisci il consumo manualmente.
          </p>
        )}
      </div>
    </div>
  );
}

export default OfferteComparator;
